// File path: /modules/elevate/frontend/src/services/api.ts

import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { Auth } from 'aws-amplify';
import awsConfig from '../aws-config';
import { retryOnceOn503 } from './retryOn503';

// Create axios instance with base URL from AWS config
const apiClient = axios.create({
  baseURL: awsConfig.API.endpoints[0].endpoint,
  timeout: 30000, // 30s timeout
});

// Token refresh state management
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

// Process queued requests after token refresh
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Add a request interceptor to add the authentication token
apiClient.interceptors.request.use(
  async (config) => {
    console.log('🔑 API Request interceptor triggered for:', config.url);
    try {
      console.log('📡 Attempting to get current session...');
      const session = await Auth.currentSession();
      console.log('✅ Session retrieved successfully');

      const token = session.getAccessToken().getJwtToken();
      console.log('🎫 Token retrieved (first 50 chars):', token.substring(0, 50) + '...');

      config.headers.Authorization = `Bearer ${token}`;
      console.log('✅ Authorization header added to request');
    } catch (error) {
      console.log('❌ No authentication token available:', error);
    }
    return config;
  },
  (error) => {
    console.log('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.status, response.config.url);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    console.log('❌ API Response Error:', error.response?.status, originalRequest?.url);

    // Log more details about the error
    if (!error.response) {
      console.error('🌐 Network error details:', {
        message: error.message,
        code: error.code,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      });
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      console.log('🔄 401 detected, attempting token refresh...');

      if (isRefreshing) {
        console.log('⏳ Token refresh already in progress, queuing request...');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log('🔄 Forcing session refresh...');
        const session = await Auth.currentSession();
        const newToken = session.getAccessToken().getJwtToken();

        console.log('✅ Token refreshed successfully');

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        processQueue(null, newToken);

        return apiClient(originalRequest);
      } catch (refreshError) {
        console.log('❌ Token refresh failed:', refreshError);

        processQueue(refreshError, null);

        localStorage.clear();
        window.location.href = '/login';

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 503 Service Unavailable (C-503UI): the authorizer's identity store is still
    // resuming (Aurora paused) or a handler is briefly unavailable. Wait Retry-After and retry
    // once; a second 503 rejects with a readable message — never empty fallback data.
    if (error.response?.status === 503) {
      console.warn('🚧 Service unavailable (503) - retrying once:', originalRequest?.url);
      return retryOnceOn503(apiClient, error);
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.error('🚫 Access forbidden - insufficient permissions');
    }

    // Handle 404 Not Found (likely unimplemented endpoints)
    if (error.response?.status === 404) {
      console.warn('🔍 Endpoint not found - may not be implemented yet:', originalRequest?.url);
    }

    // Handle 502 (Lambda not deployed or misconfigured)
    if (error.response?.status === 502) {
      console.warn('🚧 Service unavailable - Lambda may not be deployed:', originalRequest?.url);
    }

    return Promise.reject(error);
  }
);

// Common types
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

// API helper functions
export const apiHelpers = {
  /**
   * Build query string from parameters object
   */
  buildQueryString(params: Record<string, any>): string {
    const cleanParams = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&');
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      })
      .join('&');

    return cleanParams ? `?${cleanParams}` : '';
  },

  /**
   * GET request helper with improved error handling and fallbacks
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await apiClient.get<T>(url, config);
      return response.data;
    } catch (error: any) {
      console.error(`GET ${url} failed:`, error.response?.data || error.message);

      // For development: return empty/default data for unimplemented endpoints.
      // Not for 503 (C-503UI): that is a real outage, retried once by the interceptor, and the
      // page must show it rather than render empty data.
      if (error.response?.status === 404 || error.response?.status === 502) {
        console.warn(`🚧 Returning fallback data for ${url}`);
        return apiHelpers.getFallbackData<T>(url);
      }

      throw error;
    }
  },

  /**
   * Provide fallback data for unimplemented endpoints during development
   */
  getFallbackData<T>(url: string): T {
    if (url.includes('/notifications')) {
      return (url.includes('unread-count') ? { count: 0 } : { notifications: [], pagination: { total: 0, limit: 50, offset: 0 } }) as T;
    }

    if (url.includes('/schools/') && url.includes('/users')) {
      return { users: [], pagination: { total: 0, limit: 100, offset: 0 } } as T;
    }

    if (url.includes('/schools/') && url.includes('/stats')) {
      return {
        totalTeachers: 0,
        totalCoaches: 0,
        evaluationsThisMonth: 0,
        avgTeacherScore: 0
      } as T;
    }

    if (url.includes('/schools/') && !url.includes('/users') && !url.includes('/stats')) {
      return {
        id: '',
        name: 'Loading...',
        district_id: '',
        state_id: '',
        created_at: '',
        updated_at: ''
      } as T;
    }

    if (url.includes('/evaluations')) {
      return { evaluations: [], pagination: { total: 0, limit: 10, offset: 0 } } as T;
    }

    // Default fallback
    return {} as T;
  },

  /**
   * POST request helper with improved error handling
   */
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await apiClient.post<T>(url, data, config);
      return response.data;
    } catch (error: any) {
      console.error(`POST ${url} failed:`, error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * PUT request helper with improved error handling
   */
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await apiClient.put<T>(url, data, config);
      return response.data;
    } catch (error: any) {
      console.error(`PUT ${url} failed:`, error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * DELETE request helper with improved error handling and optional typed response
   */
  async delete<T = void>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await apiClient.delete<T>(url, config);
      // If T is void, return undefined, otherwise return the response data
      return response.data as T;
    } catch (error: any) {
      console.error(`DELETE ${url} failed:`, error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * PATCH request helper with improved error handling
   */
  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await apiClient.patch<T>(url, data, config);
      return response.data;
    } catch (error: any) {
      console.error(`PATCH ${url} failed:`, error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Get the base URL for the API
   */
  getBaseUrl(): string {
    return apiClient.defaults.baseURL || process.env.REACT_APP_API_URL || 'https://api.lumilearn.app';
  }
};

// Auth API
export const authApi = {
  login: async (username: string, password: string) => {
    return await Auth.signIn(username, password);
  },

  logout: async () => {
    return await Auth.signOut();
  },

  forgotPassword: async (username: string) => {
    return await Auth.forgotPassword(username);
  },

  resetPassword: async (username: string, code: string, newPassword: string) => {
    return await Auth.forgotPasswordSubmit(username, code, newPassword);
  },

  completeNewPassword: async (user: any, newPassword: string) => {
    return await Auth.completeNewPassword(user, newPassword);
  },

  getCurrentUser: async () => {
    return await Auth.currentAuthenticatedUser();
  },

  refreshSession: async () => {
    try {
      const session = await Auth.currentSession();
      return session.getAccessToken().getJwtToken();
    } catch (error) {
      console.error('Failed to refresh session:', error);
      throw error;
    }
  }
};

export default apiClient;